import type { LocaleCatalog } from "../types";

const n = (event: { payload?: Record<string, string | number | boolean> }, key: string, fallback: string | number) =>
  event.payload?.[key] ?? fallback;

export const de = {
  ui: {
    brandTagline: "INTERSYSTEM-KARTOGRAFIE",
    introSub: "KX / MIDGARD-RELAIS",
    introTitle: "DER RAUM ZWISCHEN SYSTEMEN",
    systemsNominal: "KONSTELLATION NOMINAL",
    command: "KOMMANDO",
    commandPaletteLabel: "K·NEXUS-Befehlspalette",
    commandPlaceholder: "Suche Systeme, Welten und orbitale Identitäten…",
    commandEmpty: "Kein Signal an diesen Koordinaten.",
    navHeading: "NAVIGATION",
    entitiesHeading: "ENTITÄTEN & ARTEFAKTE",
    orbitalHeading: "ORBITALE IDENTITÄTEN",
    returnToCosmos: "Zurück zur Karte",
    returnToCosmosDetail: "Zur vollständigen Intersystem-Karte zurückkehren",
    navigate: "NAVIGIEREN",
    focus: "FOKUS",
    close: "SCHLIESSEN",
    allSystems: "ALLE SYSTEME",
    map: "KARTE",
    frame: "RAHMEN",
    local: "LOKAL",
    cosmos: "KOSMOS",
    find: "SUCHE",
    sound: "TON",
    access: "ZUGANG",
    focusLabel: "FOKUS",
    edge: "EDGE",
    data: "DATEN",
    hybridMock: "HYBRID / MOCK",
    live: "LIVE",
    fallback: "FALLBACK",
    activity: "AKTIVITÄT",
    liveEventFabric: "LIVE-SIGNALGEWEBE",
    click: "KLICK",
    escape: "ESC",
    hideUi: "UI AUSBLENDEN",
    closeInspector: "Inspektor schließen",
    expandInspector: "Inspektordetails erweitern",
    collapseInspector: "Inspektordetails einklappen",
    stateVector: "STATUSVEKTOR",
    lastSignal: "LETZTES SIGNAL",
    revision: "REVISION",
    objects: "OBJEKTE",
    class: "KLASSE",
    connectedEntities: "VERBUNDENE ENTITÄTEN",
    systemComponents: "SYSTEMKOMPONENTEN",
    recentSignals: "AKTUELLE SIGNALE",
    openSystem: "ZUM SYSTEM WECHSELN",
    unknown: "UNBEKANNT",
    orbitalIdentity: "ORBITALE IDENTITÄT",
    parentSystem: "ÜBERGEORDNETES SYSTEM",
    orbitalClass: "ORBITALKLASSE",
    languageSelector: "Sprache",
    switchLanguage: (value) => `Sprache auf ${value} umstellen`,
    semanticMap: "Konstellationsnavigator",
    semanticMapHint: "Tastaturzugängliche Karte der lebenden Konstellation",
    accessibleObservatory: "Barrierefreies Observatorium",
    accessibleObservatoryOn: "Barrierefreies Observatorium aktiviert",
    accessibleObservatoryOff: "Barrierefreies Observatorium deaktiviert",
    navProduct: "Produkt",
    navHowItWorks: "Wie es funktioniert",
    navSolutions: "Lösungen",
    navResources: "Ressourcen",
    navAboutUs: "Über uns",
    getStarted: "Loslegen",
    heroHeadlineLine1: "Zwischen Welten.",
    heroHeadlineLine2Prefix: "Betritt den",
    heroHeadlineLine2Accent: "Nexus.",
    heroDescription:
      "Ein Live-Relais von kaim.dev — Manifeste, Verbindungen und Zustand als ein navigierbarer Kosmos.",
    startMapping: "Nexus betreten",
    watchVision: "Kern fokussieren",
    demoTour: "Erkundungsroute",
    exitDemoTour: "Erkundung beenden",
    heroEyebrow: "MIDGARD RELAY / LIVE",
    nowLabel: "JETZT",
    overviewLabel: "Atlas",
  },
  nodes: {
    kaimDev: {
      label: "KAIM.DEV",
      eyebrow: "IDENTITÄT / PORTFOLIO",
      description: "Zentrale öffentliche Identitätsschicht und Portfolio-Frontier des gesamten Ökosystems.",
    },
    goals: {
      label: "K·100",
      eyebrow: "ZIELE / ZUKÜNFTE",
      description: "Ein lebender Atlas aus hundert Zielen, Prognosen und Prediction-Market-Zustand.",
    },
    build: {
      label: "K·BUILD",
      eyebrow: "HAUS / MICHAŁOWICE",
      description: "End-to-End-System für Wissen, Status und Prozess des Hausbaus in Michałowice.",
    },
    photos: {
      label: "PHOTOS",
      eyebrow: "ARCHIV / ERINNERUNG",
      description: "photos.kaim.dev — die Fotoarchiv-Plattform für private, benannte Instanzen.",
    },
    breakglass: {
      label: "BREAKGLASS",
      eyebrow: "SATIRE / KI",
      description: "Satirische Notfalloberfläche zum Thema übermäßige KI-Exposition.",
    },
    home: {
      label: "HOME",
      eyebrow: "AUTOMATION / PHYSISCH",
      description: "Zukünftige Home-Assistant-Brücke und Telemetrie des physischen Hauszustands.",
    },
    okolica: {
      label: "OKOLICA DZIAŁKI",
      eyebrow: "STANDORTINTELLIGENZ",
      description: "Standortintelligenz auf Parzellenebene: Planung, Mobilität, Gelände und Umweltkontext.",
    },
    michalowice: {
      label: "MICHAŁOWICE",
      eyebrow: "GRUNDSTÜCK / 841 M²",
      description: "Zentraler physischer Anker: das gewählte Grundstück und der zukünftige Hausstandort.",
    },
    houseGoal: {
      label: "HAUSZIEL",
      eyebrow: "ZIEL / WOHNEN",
      description: "Langfristiges Ziel, das den Weg vom Grundstückskauf bis zum fertigen Haus beschreibt.",
    },
    house: {
      label: "HAUS",
      eyebrow: "PHYSISCH / WOHNEN",
      description: "Das physische Haus, das auf dem Grundstück in Michałowice geplant und gebaut wird.",
    },
    marketEngine: {
      label: "MARKT-ENGINE",
      eyebrow: "LMSR / INTEGRITÄT",
      description: "Sequenzierter LMSR-Zustand, Ledger und Portfolioprojektion hinter K·100 Futures.",
    },
    portfolioFrontier: {
      label: "PORTFOLIO FRONTIER",
      eyebrow: "WORKER / DEPLOYMENT",
      description: "Cloudflare-Worker-Bereitstellungsoberfläche für das öffentliche Portfolio.",
    },
    photoArchive: {
      label: "KOLLEKTION 01",
      eyebrow: "20.769 OBJEKTE",
      description: "Indizierte Quellsammlung hinter der fotografischen Archivoberfläche.",
    },
    aferyLekarskie: {
      label: "ÄRZTESKANDALE",
      eyebrow: "SATIRE / GENERATOR",
      description: "Ein leichter statischer Generator fiktiver Ärzteskandale ohne Datenbank oder Benutzerkonten.",
    },
  },
  orbitals: {
    goalsDo: {
      label: "INTEGRITÄTSKOORDINATOR",
      eyebrow: "DURABLE OBJECT / SEQUENZER",
      description:
        "Zustands-Sequenzer für geordnete Trades, Integritätsprüfungen und deterministische Marktprojektionen.",
    },
    goalsD1: {
      label: "MARKET D1",
      eyebrow: "ZUSTAND / POSITIONEN / LEDGER",
      description: "Relationale Speicherschicht für Konten, Positionen, Ledger und Marktaggregate.",
    },
    goalsFuturesUi: {
      label: "FUTURES UI",
      eyebrow: "MARKTOBERFLÄCHE / INTERAKTION",
      description: "Benutzeroberfläche für Preise, Positionen, Portfolios und Guidance.",
    },
    goalsLedger: {
      label: "LEDGER-STROM",
      eyebrow: "APPEND-ONLY / TRADES",
      description: "Append-only-Transaktionshistorie und Eventstrom zur Rekonstruktion des Marktzustands.",
    },
    kaimWorker: {
      label: "PORTFOLIO FRONTIER",
      eyebrow: "CLOUDFLARE WORKER / EDGE",
      description: "Primäre Edge-Laufzeit für Portfolio und öffentliche Routen.",
    },
    kaimStudioApi: {
      label: "STUDIO API",
      eyebrow: "INHALT / STUDIO-OBERFLÄCHE",
      description: "Interne API-Schicht für Inhaltsoperationen und zukünftige Studio-Fähigkeiten.",
    },
    kaimAccess: {
      label: "ZERO TRUST ACCESS",
      eyebrow: "ZUGANG / SCHUTZ",
      description: "Zugangsschicht zum Schutz privater Interfaces und interner Oberflächen.",
    },
    kaimDesignSystem: {
      label: "DESIGN-SCHICHT",
      eyebrow: "TYPOGRAFIE / MOTION / TOKENS",
      description: "Gemeinsame visuelle Sprache für Layout, Motion und Typografie im gesamten Ökosystem.",
    },
    kaimNexusConnector: {
      label: "NEXUS CONNECTOR",
      eyebrow: "MCP / API / GRAPH",
      description:
        "Maschinenorientiertes Relais zwischen kaim.dev-Welten: Manifeste werden zu Koordinaten, Service-Kanten zu Routen.",
    },
    photosArchiveIndex: {
      label: "ARCHIVINDEX",
      eyebrow: "MATERIALISIERT / SEARCH",
      description: "Materialisierter Fotoindex und Summary aus dem normalen Archiv-Indexierungsprozess.",
    },
    photosObjectStore: {
      label: "OBJECT STORE",
      eyebrow: "MEDIA / STORAGE",
      description: "Speicherschicht hinter Photos; Nexus konsumiert nur Aggregate und listet Objekte nie on-demand.",
    },
    photosRozaInstance: {
      label: "RÓŻA-INSTANZ",
      eyebrow: "roza.photos.kaim.dev / PRIVAT",
      description:
        "Konkrete private Photos-Instanz, nur als redigierte Health-, Revisions- und Index-Metadaten dargestellt.",
    },
    homeAssistant: {
      label: "HOME ASSISTANT",
      eyebrow: "AUTOMATION / CONTROL",
      description: "Künftige lokale Steuerungsebene für Geräte, Szenen und Hauszustand.",
    },
    homePhysicalHost: {
      label: "PHYSISCHER HOST",
      eyebrow: "NETTOP / LOCAL",
      description: "Lokaler Host für Home Assistant und die Brücke physischer Zustände ins Ökosystem.",
    },
    homeRemoteAccess: {
      label: "REMOTE ACCESS",
      eyebrow: "smarthome.kaim.dev / EDGE",
      description: "Geschützter Fernzugang ohne rohe lokale Telemetrie an Nexus auszuliefern.",
    },
    okolicaCadastrePlanning: {
      label: "KATASTER + PLANUNG",
      eyebrow: "ULDK / MPZP",
      description: "Materialisierte Flurstücks- und Planungskontexte für das ausgewählte Grundstück.",
    },
    okolicaTerrainEnvironment: {
      label: "GELÄNDE + UMWELT",
      eyebrow: "GEFÄLLE / RISIKO / KONTEXT",
      description: "Vorberechnete Gelände- und Umweltzusammenfassung für die Grundstücksanalyse.",
    },
    okolicaMobility: {
      label: "MOBILITÄT",
      eyebrow: "STRASSEN / ZUGANG",
      description: "Vorberechnete Zugangs- und Mobilitätszusammenfassung statt Live-Routing aus Nexus.",
    },
    okolicaEvidenceExport: {
      label: "EVIDENCE EXPORT",
      eyebrow: "GEOJSON / SNAPSHOT",
      description: "Versioniertes Evidence-Artefakt für K·BUILD ohne erneute Standortanalyse.",
    },
    breakglassEdgeRuntime: {
      label: "EDGE RUNTIME",
      eyebrow: "CLOUDFLARE / DELIVERY",
      description: "Öffentliche Edge-Laufzeit für Breakglass-Szenarien und statische Systemmetadaten.",
    },
    breakglassScenarioEngine: {
      label: "SZENARIO-ENGINE",
      eyebrow: "REGISTER / MODI",
      description: "Szenario-Register und deterministische Moduskonfiguration, nur als Summary an Nexus exponiert.",
    },
    buildBrief: {
      label: "ARCHITEKTURBRIEFING",
      eyebrow: "PROGRAMM / ANFORDERUNGEN",
      description: "Strukturiertes Briefing zu Raumintention, Programm und Einschränkungen.",
    },
    buildDocuments: {
      label: "DOKUMENTENATLAS",
      eyebrow: "DATEIEN / ENTSCHEIDUNGEN / EVIDENZ",
      description: "Wissensschicht, die Dokumente, Entscheidungen, Meilensteine und Prozessspuren verknüpft.",
    },
    buildPlotIntel: {
      label: "GRUNDSTÜCKS-INTELLIGENZ",
      eyebrow: "GELÄNDE / PLANUNG / KONTEXT",
      description: "Analytischer Parzellenkontext aus Okolica Działki und verwandten Planungsquellen.",
    },
    buildTimeline: {
      label: "MEILENSTEIN-TIMELINE",
      eyebrow: "PROZESS / PHASEN",
      description: "Meilensteinsystem für Projektphasen, Genehmigungen und Bauvorbereitung.",
    },
  },
  events: {
    "build.brief.updated": (e) => ({
      title: `Architekturbriefing v${n(e, "version", 3)} bestätigt`,
      detail: "Das Programm wechselte in einen strukturierten Vorentwurfszustand.",
    }),
    "goal.progress.updated": (e) => ({
      title: `Ziel #${n(e, "goal", 34)} → ${n(e, "progress", 48)}%`,
      detail: "Fortschrittsprojektion aktualisiert.",
    }),
    "portfolio.deployed": (e) => ({
      title: "portfolio-frontier bereitgestellt",
      detail: `Produktive Edge-Revision ${n(e, "revision", "unbekannt")}.`,
    }),
    "photos.indexed": (e) => ({
      title: "Kollektionsmanifest neu aufgebaut",
      detail: `${Number(n(e, "count", 0)).toLocaleString("de-DE")} Quellobjekte indiziert.`,
    }),
    "home.simulation": () => ({
      title: "Home-Knoten wartet auf physischen Host",
      detail: "Die Telemetrie bleibt im aktuellen Prototyp simuliert.",
    }),
    "market.integrity": () => ({
      title: "Marktintegritäts-Koordinator ist gesund",
      detail: "Sequenzierte Zustandsprojektion verfügbar.",
    }),
    "build.state.sampled": () => ({
      title: "Bauzustand synchronisiert",
      detail: "K·BUILD hat einen neuen materialisierten Bausnapshot veröffentlicht.",
    }),
    "goals.market.sampled": () => ({
      title: "K·100-Markt-Snapshot abgeglichen",
      detail: "Die Mock-Marktprojektion entsprach dem aktuellen Ledger-Stand.",
    }),
    "portfolio.edge.sampled": () => ({
      title: "Edge-Deployment-Heartbeat",
      detail: "portfolio-frontier antwortete innerhalb der nominalen Hülle.",
    }),
    "photos.manifest.sampled": () => ({
      title: "Archivindex-Heartbeat",
      detail: "Das Sammlungsmanifest bleibt verfügbar.",
    }),
    "home.awaiting-host": () => ({
      title: "Physischer Knoten noch nicht bereitgestellt",
      detail: "Dies ist in der aktuellen Mock-Phase erwartbar.",
    }),
    "location.context.sampled": () => ({
      title: "Parzellenkontext aktualisiert",
      detail: "Die Mock-Projektion der Standortintelligenz wurde aktualisiert.",
    }),
  },
} satisfies LocaleCatalog;
